import { useState, useCallback, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Fade, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import UsersTagCard from '../../shared/usersTagCard';
import ColorBox from '../../shared/colorBox';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import NotificationCard from '../../shared/notificationCard';
import CustomFormDrawer from '../../shared/customFormDrawer';
import {
  api_tag_category,
  api_tagged_users,
  api_assign_users_to_tag,
} from '../../../constants/apiRoutes';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import { ASSIGN_TAG_USERS_FORM_FIELD } from '../../../constants/tags';
import { useTranslations } from '../../../utility/useTranslations';
import {
  isUserProfileAccessibleCheck,
  hasAuthPermissionCheck,
} from '../../../utility/permissions';
import { isArrayEmpty, getItemById } from '../../../utility/helpers';
import { getInitialAssignedTagUsersData } from '../../../utility/tagUtils';
import http from '../../../utility/http';
import { useAvailableUsers } from '../../../utility/hooks';
import {
  prepareUsersForUpdate,
  showSuccessMessage,
} from '../../../utility/uiUtils';

const useStyles = makeStyles(({ spacing }) => ({
  tags: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
    gridGap: spacing(2),
  },
  categoryWrapper: {
    display: 'flex',
    alignItems: 'center',
    margin: spacing(8, 0, 4, 0),
    width: '100%',
  },
  colorBox: {
    flexShrink: 0,
    marginRight: spacing(3),
  },
  drawerContent: {
    height: '100%',
  },
}));

const TagCategoryPage = ({
  navigate,
  auth,
  accessControl,
  organizationSettings,
  ...rest
}) => {
  const { categoryId } = useParams();
  const translations = useTranslations(APP_PAGES.TAG_CATEGORY);
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [tagsWithUsers, setTagsWithUsers] = useState(null);
  const [isAssignDialogOpened, setIsAssignDialogOpened] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [tagUsers, setTagUsers] = useState([]);

  const canManageTagUsers = hasAuthPermissionCheck(
    PERMISSION_CODES.TAGS_USERS_MANAGE,
    accessControl
  );
  const initialAssignedTagUsersData = getInitialAssignedTagUsersData(tagUsers);

  const allUsersParams = useMemo(
    () =>
      canManageTagUsers
        ? {
            [PARAMS.RBAC_SCOPE_PERMISSION]: PERMISSION_CODES.TAGS_USERS_MANAGE,
          }
        : {},
    [canManageTagUsers]
  );

  const allUsers = useAvailableUsers(allUsersParams, null, true);

  const goToProfilePage = useCallback(
    user => () => navigate(`/people/${user.id}`),
    [navigate]
  );

  const canAccessUserProfileCheck = useCallback(
    userId => isUserProfileAccessibleCheck(accessControl, auth.id, userId),
    [auth, accessControl]
  );

  const onOpenAssignTagUsers = tag => () => {
    const users = getItemById(tagsWithUsers || [], tag.id)?.users || [];
    setTagUsers(users);
    setSelectedTag(tag);
    setIsAssignDialogOpened(true);
  };

  const onCloseAssignTagUsers = () => {
    setIsAssignDialogOpened(false);
    setSelectedTag(null);
    setTagUsers([]);
  };

  const onSaveTagUsers = async ({ users }) => {
    const preparedUsers = prepareUsersForUpdate(
      allUsers,
      users,
      tagUsers,
      selectedTag.id,
      'tag'
    );

    await http.post(api_assign_users_to_tag(selectedTag.id), {
      users: preparedUsers,
    });

    showSuccessMessage(translations.manageTagUsersForm.successMessage);
    const { data } = await http.get(api_tagged_users(categoryId));
    setTagsWithUsers(data);
    return Promise.resolve();
  };

  const getCategory = useCallback(async () => {
    try {
      const currentCategory = await http.get(api_tag_category(categoryId));

      setCategory(currentCategory.data);
      if (!isArrayEmpty(currentCategory?.data?.tags)) {
        const categoryTagsWithUsers = await http.get(
          api_tagged_users(categoryId)
        );
        setTagsWithUsers(categoryTagsWithUsers.data);
      } else {
        setTagsWithUsers(null);
      }
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    getCategory();
  }, [getCategory, categoryId]);

  return (
    <PageContainer
      {...rest}
      translations={translations}
      navigate={navigate}
      auth={auth}
      accessControl={accessControl}
      organizationSettings={organizationSettings}
      shouldPassProps={false}
    >
      <Fade in appear>
        <div>
          {!isLoading && (
            <div className={classes.categoryWrapper}>
              <ColorBox className={classes.colorBox} bgColor={category.color} />
              <TextBoxWithTooltip variant="h5" text={category.name} />
            </div>
          )}
          {!isLoading && tagsWithUsers && (
            <div className={classes.tags}>
              {tagsWithUsers.map(tag => (
                <UsersTagCard
                  key={`tag_${tag.id}`}
                  manageUsersButtonLabel={translations.manageUsersButtonLabel}
                  tag={tag}
                  color={category.color}
                  canAccessUserProfileCheck={canAccessUserProfileCheck}
                  hasManageUsersButton={canManageTagUsers}
                  onManageUsers={onOpenAssignTagUsers(tag)}
                  onGoToUserProfile={goToProfilePage}
                />
              ))}
            </div>
          )}
          {!isLoading && !tagsWithUsers && (
            <NotificationCard content={translations.noData} />
          )}
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            translations={translations.manageTagUsersForm}
            isOpened={isAssignDialogOpened}
            initialData={initialAssignedTagUsersData}
            fields={ASSIGN_TAG_USERS_FORM_FIELD}
            allUsers={allUsers}
            onClose={onCloseAssignTagUsers}
            onSave={onSaveTagUsers}
            hasCancelButton
            hideDelete
            isInitialValid
          />
        </div>
      </Fade>
    </PageContainer>
  );
};

TagCategoryPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default memo(TagCategoryPage);
